import React, { useEffect, useState, useCallback, useRef } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';

import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import BatmanSection from './components/BatmanSection';
import ServicesSection from './components/ServicesSection';
import StoreSection from './components/StoreSection';
import SMMSection from './components/SMMSection';
import ContactSection from './components/ContactSection';
import DonateSection from './components/DonateSection';
import ScrollProgress from './components/ScrollProgress';
import ChatWidget from './components/ChatWidget';
import StorePage from './pages/StorePage';
import ServicesPage from './pages/ServicesPage';
import SMMPage from './pages/SMMPage';
import AdminChatPage from './pages/AdminChatPage';

import { navItems } from './mock';

// ---------- Background star field ----------
function StarField() {
  const stars = React.useMemo(() => {
    const arr = [];
    for (let i = 0; i < 80; i++) {
      arr.push({
        id: i,
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: Math.random() * 2 + 0.5,
        delay: Math.random() * 4,
        duration: 3 + Math.random() * 5,
      });
    }
    return arr;
  }, []);
  return (
    <div className="star-field">
      {stars.map((s) => (
        <div
          key={s.id}
          className="star"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

function TopNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const [active, setActive] = useState('hero');

  useEffect(() => {
    if (!isHome) return;
    const ids = navItems.map((n) => n.id);
    const onScroll = () => {
      const y = window.scrollY + window.innerHeight / 3;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= y) current = id;
      }
      setActive(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  const goTo = (id) => {
    if (!isHome) {
      navigate('/', { state: { scrollTo: id } });
      return;
    }
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 40, behavior: 'smooth' });
  };

  return (
    <div className="nav-wrap">
      <div className="nav-inner">
        {navItems.map((n) => (
          <button
            key={n.id}
            className={`nav-item ${isHome && active === n.id ? 'active' : ''}`}
            onClick={() => goTo(n.id)}
          >
            {n.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div>&copy; {new Date().getFullYear()} Likith &mdash; All rights reserved</div>
      <div>Built in the dark &mdash; stay sharp</div>
    </footer>
  );
}

// ---------- Home (single-page scroll) ----------
function Home() {
  const location = useLocation();
  const sectionIds = React.useMemo(() => navItems.map((n) => n.id), []);
  const currentIdxRef = useRef(0);
  const lockUntilRef = useRef(0);

  // Reveal-on-scroll observer
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('in');
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // Scroll-to from router state (e.g., when coming back from /store)
  useEffect(() => {
    const id = location.state && location.state.scrollTo;
    if (id) {
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) window.scrollTo({ top: el.offsetTop - 40, behavior: 'smooth' });
      }, 100);
    }
  }, [location.state]);

  // Track current section index based on real scroll (not during programmatic scroll)
  useEffect(() => {
    const compute = () => {
      if (Date.now() < lockUntilRef.current) return;
      const y = window.scrollY + window.innerHeight * 0.4;
      let idx = 0;
      for (let i = 0; i < sectionIds.length; i++) {
        const el = document.getElementById(sectionIds[i]);
        if (el && el.offsetTop <= y) idx = i;
      }
      currentIdxRef.current = idx;
    };
    window.addEventListener('scroll', compute, { passive: true });
    compute();
    return () => window.removeEventListener('scroll', compute);
  }, [sectionIds]);

  const scrollToIndex = useCallback((i) => {
    const clamped = Math.max(0, Math.min(sectionIds.length - 1, i));
    const el = document.getElementById(sectionIds[clamped]);
    if (!el) return;
    currentIdxRef.current = clamped;
    lockUntilRef.current = Date.now() + 700;
    window.scrollTo({ top: el.offsetTop - 40, behavior: 'smooth' });
  }, [sectionIds]);

  // Keyboard auto-scroll
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
      if (e.target && e.target.isContentEditable) return;

      const downKeys = ['ArrowDown', 'PageDown'];
      const upKeys = ['ArrowUp', 'PageUp'];

      if (downKeys.includes(e.key)) {
        e.preventDefault();
        if (Date.now() < lockUntilRef.current) return;
        window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
      } else if (upKeys.includes(e.key)) {
        e.preventDefault();
        if (Date.now() < lockUntilRef.current) return;
        window.scrollBy({ top: -window.innerHeight, behavior: 'smooth' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [scrollToIndex]);

  return (
    <>
      <ScrollProgress />
      <HeroSection />
      <AboutSection />
      <BatmanSection />
      <ServicesSection />
      <StoreSection />
      <SMMSection />
      <ContactSection />
      <DonateSection />
      <Footer />
    </>
  );
}

export function SubPageShell({ title, kicker, children }) {
  return (
    <div style={{ minHeight: '100vh', position: 'relative', zIndex: 3 }}>
      <ScrollProgress />
      <div className="subpage-head" style={{ padding: '120px 96px 40px' }}>
        <Link
          to="/"
          className="mono"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 11,
            letterSpacing: '0.3em',
            color: 'rgba(245,245,245,0.55)',
            textTransform: 'uppercase',
            textDecoration: 'none',
            marginBottom: 24,
          }}
        >
          {'\u2190'} Back to home
        </Link>
        <div className="section-label" style={{ marginBottom: 16 }}>{kicker}</div>
        <h1 className="section-title" style={{ margin: 0, maxWidth: 900 }}>{title}</h1>
      </div>
      {children}
      <Footer />
    </div>
  );
}

function App() {
  return (
    <div className="App" style={{ background: '#000', minHeight: '100vh' }}>
      <BrowserRouter>
        <div className="noise-overlay" />
        <StarField />
        <TopNav />
        <ChatWidget />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/store" element={<StorePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/smm" element={<SMMPage />} />
          <Route path="/admin" element={<AdminChatPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
